/**
 * Context menu of Azure Storage tree nodes (spec §5.2): service folder →
 * Refresh; container (canonical or pinned) → Open · Copy URL (SAS-free) · Pin
 * / Unpin · Refresh · Open in Azure portal (only when the saved connection
 * carries `azureResourceId`, i.e. it came from a Browse Azure pick). Items of
 * later slices are absent, never disabled placeholders.
 */
import type { ContextMenuAction } from '../../explorer/models/TreeModel';
import type { PinnedStorageNode, StorageActionNode, StorageResourceNode, StorageServiceFolderNode } from '../../explorer/models/nodeTypes';
import { toast } from '../../../components/ui/toaster';
import { writeClipboardText } from '../../../lib/clipboard';
import { getConnectionById } from '../../connections/utils/connectionLookup';
import type { PinnedObjectRef } from '../../../types/electron';
import { openStorageExplorerTab } from '../../workspace/storage-explorer/utils/openStorageExplorerTab';
import { storageResourceUrl } from '../../workspace/storage-explorer/utils/storageUrls';
import { usePinnedObjectsStore } from '../store/pinnedObjectsStore';
import { pinRefKey } from './pinnedObjects/pinRef';
import { getStorageMenuItems } from './storageTreeExtensions';
import { executeS2StorageTreeAction, isS2StorageTreeAction } from '../../workspace/storage-explorer/mutations/treeMenuItems';
import { executeS5MenuAction, type QueueMenuContext } from '../../workspace/storage-explorer/messages/queueMenuItems';
import { getStorageAccountInfoSnapshot } from '../store/storageAccountInfoStore';
import { s3MenuItems } from '../../workspace/storage-explorer/properties/storageS3MenuItems';
import { useStorageDialogStore, type StorageDialogKind } from '../../workspace/storage-explorer/properties/dialogs/storageDialogStore';
import { executeS4StorageTreeAction } from '../../workspace/storage-explorer/entities/tree/tableMenuItems';
import { executeS7StorageTreeAction } from '../../workspace/storage-explorer/files/tree/shareMenuItems';
import { executeS8StorageTreeAction } from '../../workspace/storage-explorer/hns/tree/hnsTreeExtension';

export type StorageContextMenuActionId = 'open' | 'copy-url' | 'pin' | 'unpin' | 'refresh' | 'open-in-portal' | 's3-properties' | 's3-public-access' | 's3-access-policies' | 's3-sas';

export type StorageMenuNode = StorageResourceNode | StorageServiceFolderNode | StorageActionNode | PinnedStorageNode;

const separator: ContextMenuAction = { id: 'separator', label: '', isSeparator: true };

export function storagePinRefForNode(node: StorageMenuNode): Extract<PinnedObjectRef, { kind: 'storage' }> | null {
  if (node.type === 'pinned-storage') return node.pinRef;
  if (node.type === 'storage-service-folder' || node.type === 'storage-action') return null;
  return { kind: 'storage', resource: node.metadata.resource, label: node.name };
}

export function isStoragePinned(connectionId: string, ref: PinnedObjectRef): boolean {
  const key = pinRefKey(ref);
  const pins: readonly PinnedObjectRef[] = usePinnedObjectsStore.getState().byConnection[connectionId]?.pinnedObjects ?? [];
  return pins.some((pin) => pinRefKey(pin) === key);
}

/** C2 N16: Open shows what it opens — a table's grid, a queue's messages, a folder view for containers and shares. */
function openIcon(kind: string): string {
  if (kind === 'table') return 'table';
  if (kind === 'queue') return 'message-square';
  return 'folder-open';
}

export function generateStorageContextMenuActions(node: StorageMenuNode): ContextMenuAction[] {
  if (node.type === 'storage-action') return [];
  const readOnly = getConnectionById(node.metadata.connectionId).connection?.readOnly === true;
  const extensionItems = getStorageMenuItems(node, { readOnly });
  if (node.type === 'storage-service-folder') {
    return [...extensionItems, ...(extensionItems.length > 0 ? [separator] : []), { id: 'refresh', label: 'Refresh', icon: 'refresh' }];
  }
  const { connectionId } = node.metadata;
  const ref = storagePinRefForNode(node);
  const pinned = ref ? isStoragePinned(connectionId, ref) : false;
  const connection = getConnectionById(connectionId).connection;
  const hasResourceId = !!connection?.azureResourceId;
  const capabilities = getStorageAccountInfoSnapshot(connectionId)?.capabilities ?? {};
  const actions: ContextMenuAction[] = [
    { id: 'open', label: 'Open', icon: openIcon(node.metadata.resource.kind) },
    ...s3MenuItems(node, { readOnly: connection?.readOnly === true, capabilities }),
    { id: 'copy-url', label: 'Copy URL', icon: 'link-2' },
    pinned ? { id: 'unpin', label: 'Unpin', icon: 'pin-off' } : { id: 'pin', label: 'Pin to top', icon: 'pin' },
    separator,
    { id: 'refresh', label: 'Refresh', icon: 'refresh' },
  ];
  if (hasResourceId) actions.push(separator, { id: 'open-in-portal', label: 'Open in Azure portal', icon: 'external-link' }); // F4: not Copy URL's glyph
  return extensionItems.length > 0 ? [...extensionItems, separator, ...actions] : actions;
}

export type StorageContextMenuDeps = {
  onRefresh: (nodeId: string) => void;
  track: (eventName: string, data?: Record<string, string | number | boolean>) => void;
  openDialog?: QueueMenuContext['openDialog'];
};

export function azurePortalResourceUrl(resourceId: string): string {
  return `https://portal.azure.com/#@/resource${resourceId.startsWith('/') ? resourceId : `/${resourceId}`}`;
}

export function executeStorageContextMenuAction(actionId: string, node: StorageMenuNode, deps: StorageContextMenuDeps): void {
  if (isS2StorageTreeAction(actionId)) {
    void executeS2StorageTreeAction(actionId, node, { refresh: (nodeId) => deps.onRefresh(nodeId ?? node.id) });
    return;
  }
  if (executeS5MenuAction(actionId, node, deps.openDialog ? { openDialog: deps.openDialog, track: deps.track } : undefined)) return;
  if (executeS4StorageTreeAction(actionId, node, deps)) return;
  if (executeS7StorageTreeAction(actionId, node)) return;
  if (executeS8StorageTreeAction(actionId, node, deps)) return;
  const { connectionId, resource } = node.metadata;
  switch (actionId as StorageContextMenuActionId) {
    case 's3-properties':
    case 's3-public-access':
    case 's3-access-policies':
    case 's3-sas': {
      const kindByAction: Record<'s3-properties' | 's3-public-access' | 's3-access-policies' | 's3-sas', StorageDialogKind> = {
        's3-properties': 'properties', 's3-public-access': 'public-access',
        's3-access-policies': 'access-policies', 's3-sas': 'sas',
      };
      const kind = kindByAction[actionId as keyof typeof kindByAction];
      useStorageDialogStore.getState().openDialog({ kind, connectionId, resource, source: 'tree' });
      return;
    }
    case 'refresh': {
      // A resource leaf refreshes its parent listing (the service folder or the pinned run).
      deps.onRefresh(node.type === 'storage-service-folder' || node.type === 'pinned-storage' ? node.id : node.parentId ?? node.id);
      return;
    }
    case 'open': {
      openStorageExplorerTab({ connectionId, root: resource, source: node.type === 'pinned-storage' ? 'pin' : 'tree' });
      return;
    }
    case 'copy-url': {
      const { connection } = getConnectionById(connectionId);
      const url = connection ? storageResourceUrl(connection, resource) : undefined;
      if (!url) {
        toast.error({ title: 'Copy URL', description: 'No endpoint is known for this resource' });
        return;
      }
      void writeClipboardText(url);
      deps.track('feature:storage_container_action', { action: 'copy_url' });
      return;
    }
    case 'pin':
    case 'unpin': {
      const ref = storagePinRefForNode(node);
      if (!ref) return;
      const store = usePinnedObjectsStore.getState();
      if (actionId === 'pin') void store.pin(connectionId, ref);
      else void store.unpin(connectionId, ref);
      deps.track('feature:object_explorer_pin', { kind: 'storage', enabled: actionId === 'pin' });
      return;
    }
    case 'open-in-portal': {
      const resourceId = getConnectionById(connectionId).connection?.azureResourceId;
      if (!resourceId) return;
      void window.electronAPI?.openExternalUrl?.(azurePortalResourceUrl(resourceId));
      deps.track('feature:storage_container_action', { action: 'open_in_portal' });
      return;
    }
  }
}
